import React, { FC, memo, useMemo } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  warrantyTextStyles,
  warrantyViewStyles
} from 'src/views/transactions/TransactionPDF/styles';
import { TemplateProductDetails } from 'src/types';
import { promoPriceToSRP } from 'src/utils';
import { RenderQuotationScreenShotCurrency } from './RenderQuotationScreenShotCurrency';

type ItemProducts = {
  quantity?: string;
  productName?: string;
  categoryName?: string;
  unitPrice?: string;
  amount?: string;
};

const styles = StyleSheet.create({
  main: {
    marginTop: 10
  },
  header: {
    height: 21,
    borderBottomWidth: 0.3,
    borderTopWidth: 1,
    backgroundColor: '#F4F4F4',
    borderColor: '#828282'
  },
  headerColumn: {
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  itemColumn: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    borderBottomWidth: 0.3,
    borderColor: '#828282'
  },
  centerColumn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomWidth: 0.3,
    borderColor: '#828282'
  },
  descriptionOrSnText: {
    maxLines: 1,
    paddingBottom: 2
  }
});

const flexSizeOfColumns = {
  qty: 0.5,
  type: 0.75,
  item: 3,
  unit_price: 1,
  amount: 1
};

const headerColumns: any = [
  { flex: flexSizeOfColumns.qty, title: 'QTY', alignItems: 'center' },
  {
    flex: flexSizeOfColumns.type,
    title: 'TYPE',
    alignItems: 'flex-start'
  },
  {
    flex: flexSizeOfColumns.item,
    title: 'PRODUCT NAME',
    alignItems: 'flex-start'
  },
  { flex: flexSizeOfColumns.unit_price, title: 'PRICE', alignItems: 'center' },
  { flex: flexSizeOfColumns.amount, title: 'TOTAL', alignItems: 'center' }
];

interface Props {
  templateProducts?: TemplateProductDetails[];
  showSRP?: boolean;
}

const alternateCategoryName = (categoryName?: string) => {
  if (!categoryName) {
    return '';
  }
  if (categoryName === 'Motherboard') {
    return 'MOBO';
  }
  return categoryName;
};

const component: FC<Props> = ({ templateProducts, showSRP = false }) => {
  const itemsPerLine = useMemo(() => {
    const templateProductsLength = templateProducts?.length ?? 0;
    const defaultLinesForItem =
      templateProductsLength > 17 ? 17 : Math.max(8, templateProductsLength);

    let newItems = Array.from({ length: defaultLinesForItem }).map(
      (_, index) => {
        if (templateProducts && templateProducts?.length > 0) {
          const item = templateProducts[index];
          if (item) {
            return {
              quantity: item?.quantity.toString(),
              productName: item?.product?.product_name,
              categoryName: item?.product?.category_name,
              unitPrice: showSRP
                ? promoPriceToSRP(item?.product?.retail_price || 0).toString()
                : item?.product?.retail_price?.toString(),
              amount: showSRP
                ? promoPriceToSRP(
                    (item?.product?.retail_price || 0) * (item?.quantity || 0)
                  ).toString()
                : (
                    (item?.product?.retail_price || 0) * (item?.quantity || 0)
                  ).toString()
            };
          }
        }
        // undefined field object for empty rows
        return {
          quantity: '',
          productName: '',
          categoryName: '',
          unitPrice: '',
          amount: ''
        };
      }
    );

    return newItems;
  }, [showSRP, templateProducts]);

  const HeaderColumn = () => {
    return (
      <View style={[styles.header, warrantyViewStyles?.row]}>
        {headerColumns.map((header: any) => (
          <View
            key={header.title}
            style={[
              styles.headerColumn,
              { flex: header.flex, alignItems: header.alignItems }
            ]}
          >
            <Text
              style={[
                warrantyTextStyles.openSansBold,
                { fontSize: 7.5, color: '#2B3445' }
              ]}
            >
              {header.title}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const ItemColumn = ({ itemProd }: { itemProd?: ItemProducts }) => {
    return (
      <View
        style={[
          warrantyViewStyles?.row,
          //we are sure that the product name that has 90 char is exceed 2 lines
          {
            height:
              itemProd?.productName && itemProd?.productName.length > 90
                ? 'auto'
                : 25
          }
        ]}
      >
        <View style={[styles.centerColumn, { flex: 0.5 }]}>
          <Text style={warrantyTextStyles.openSansRegular}>
            {itemProd?.quantity}
          </Text>
        </View>
        <View style={[styles.itemColumn, { flex: 0.75 }]}>
          {itemProd?.productName === undefined ||
          itemProd?.productName === '' ? null : (
            <Text
              style={[
                warrantyTextStyles.openSansRegular,
                { paddingHorizontal: 3, fontSize: 5.5 }
              ]}
            >{`${alternateCategoryName(itemProd?.categoryName)}`}</Text>
          )}
        </View>
        <View style={[styles.itemColumn, { flex: 3 }]}>
          {itemProd?.productName === undefined ||
          itemProd?.productName === '' ? null : (
            <Text
              style={[
                warrantyTextStyles.openSansSemiBold,
                { paddingHorizontal: 3 }
              ]}
            >{`${itemProd?.productName}`}</Text>
          )}
        </View>
        <View style={[styles.itemColumn, { flex: 1, alignItems: 'center' }]}>
          {itemProd?.unitPrice === undefined ||
          itemProd?.unitPrice === '' ? null : (
            <RenderQuotationScreenShotCurrency
              amount={+itemProd?.unitPrice || 0}
            />
          )}
        </View>
        {/* This is last column */}
        <View
          style={[
            styles.itemColumn,
            { flex: 1, alignItems: 'flex-end', paddingRight: 4 }
          ]}
        >
          {itemProd?.amount === undefined || itemProd?.amount === '' ? null : (
            <RenderQuotationScreenShotCurrency
              amount={+itemProd?.amount || 0}
            />
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={styles.main}>
      <HeaderColumn />
      {itemsPerLine?.map((item, index) => (
        <ItemColumn key={index} itemProd={item} />
      ))}
    </View>
  );
};

export const ViewQuotationItems = memo(component);
